import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import * as notificationApi from '../../utils/api/NotificationApi';

// Async thunks for template management
export const fetchTemplates = createAsyncThunk(
  'notifications/fetchTemplates',
  async ({ page = 0, size = 20, search, channel }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.getTemplates(page, size, search, channel);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createTemplate = createAsyncThunk(
  'notifications/createTemplate',
  async (templateData, { rejectWithValue }) => {
    try {
      const data = await notificationApi.createTemplate(templateData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateTemplate = createAsyncThunk(
  'notifications/updateTemplate',
  async ({ id, templateData }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.updateTemplate(id, templateData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  'notifications/deleteTemplate',
  async (id, { rejectWithValue }) => {
    try {
      console.log('deleteTemplate thunk called with ID:', id);
      const result = await notificationApi.deleteTemplate(id);
      console.log('deleteTemplate API result:', result);
      return id;
    } catch (error) {
      console.error('deleteTemplate thunk error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const toggleTemplateStatus = createAsyncThunk(
  'notifications/toggleTemplateStatus',
  async (id, { rejectWithValue }) => {
    try {
      const data = await notificationApi.toggleTemplateStatus(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add new async thunks for configuration fetching and route mapping
export const fetchConfigurationsByChannel = createAsyncThunk(
  'notifications/fetchConfigurationsByChannel',
  async ({ channel }, { rejectWithValue }) => {
    try {
      let data;
      switch (channel) {
        case 'EMAIL': {
          // Use the correct API method that exists
          const emailResponse = await notificationApi.getEmailConfigs(0, 100);
          data = emailResponse.content || emailResponse.data || emailResponse;
          break;
        }
        case 'SMS': {
          // Use the correct API method that exists
          const smsResponse = await notificationApi.getSmsConfigs(0, 100);
          data = smsResponse.content || smsResponse.data || smsResponse;
          break;
        }
        default:
          data = [];
      }
      return { channel, configurations: Array.isArray(data) ? data : [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTemplateRoutes = createAsyncThunk(
  'notifications/fetchTemplateRoutes',
  async (templateId, { rejectWithValue }) => {
    try {
      const data = await notificationApi.getTemplateRoutes(templateId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateTemplateRoutes = createAsyncThunk(
  'notifications/updateTemplateRoutes',
  async ({ templateId, routes }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.updateTemplateRoutes(templateId, routes);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunks for configuration management
export const fetchEmailConfigs = createAsyncThunk(
  'notifications/fetchEmailConfigs',
  async ({ page = 0, size = 20, applicationName }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.getEmailConfigs(page, size, applicationName);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createEmailConfig = createAsyncThunk(
  'notifications/createEmailConfig',
  async (configData, { rejectWithValue }) => {
    try {
      const data = await notificationApi.createEmailConfig(configData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateEmailConfig = createAsyncThunk(
  'notifications/updateEmailConfig',
  async ({ id, configData }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.updateEmailConfig(id, configData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteEmailConfig = createAsyncThunk(
  'notifications/deleteEmailConfig',
  async (id, { rejectWithValue }) => {
    try {
      await notificationApi.deleteEmailConfig(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleEmailConfigStatus = createAsyncThunk(
  'notifications/toggleEmailConfigStatus',
  async (id, { rejectWithValue }) => {
    try {
      const data = await notificationApi.toggleEmailConfigStatus(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const testEmailConfig = createAsyncThunk(
  'notifications/testEmailConfig',
  async ({ id, testEmail }, { rejectWithValue }) => {
    try {
      console.log('Redux testEmailConfig action started');
      console.log('Parameters:', { id, testEmail });
      console.log('API_BASE_URL:', `${process.env.REACT_APP_IBILL_GATEWAY}/notification/api/v1`);

      const data = await notificationApi.testEmailConfig(id, testEmail);
      console.log('API call successful, response:', data);
      return data;
    } catch (error) {
      console.error('Redux testEmailConfig action failed:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSmsConfigs = createAsyncThunk(
  'notifications/fetchSmsConfigs',
  async ({ page = 0, size = 20, applicationName, gateway }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.getSmsConfigs(page, size, applicationName, gateway);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createSmsConfig = createAsyncThunk(
  'notifications/createSmsConfig',
  async (configData, { rejectWithValue }) => {
    try {
      const data = await notificationApi.createSmsConfig(configData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateSmsConfig = createAsyncThunk(
  'notifications/updateSmsConfig',
  async ({ id, configData }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.updateSmsConfig(id, configData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSmsConfig = createAsyncThunk(
  'notifications/deleteSmsConfig',
  async (id, { rejectWithValue }) => {
    try {
      await notificationApi.deleteSmsConfig(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleSmsConfigStatus = createAsyncThunk(
  'notifications/toggleSmsConfigStatus',
  async (id, { rejectWithValue }) => {
    try {
      const data = await notificationApi.toggleSmsConfigStatus(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const testSmsConfig = createAsyncThunk(
  'notifications/testSmsConfig',
  async ({ id, testPhone }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.testSmsConfig(id, testPhone);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Push Configuration thunks
export const fetchPushConfigs = createAsyncThunk(
  'notifications/fetchPushConfigs',
  async ({ page = 0, size = 20, applicationName, provider }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.getPushConfigs(page, size, applicationName, provider);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createPushConfig = createAsyncThunk(
  'notifications/createPushConfig',
  async (configData, { rejectWithValue }) => {
    try {
      const data = await notificationApi.createPushConfig(configData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePushConfig = createAsyncThunk(
  'notifications/updatePushConfig',
  async ({ id, configData }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.updatePushConfig(id, configData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deletePushConfig = createAsyncThunk(
  'notifications/deletePushConfig',
  async (id, { rejectWithValue }) => {
    try {
      await notificationApi.deletePushConfig(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const togglePushConfigStatus = createAsyncThunk(
  'notifications/togglePushConfigStatus',
  async (id, { rejectWithValue }) => {
    try {
      const data = await notificationApi.togglePushConfigStatus(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const testPushConfig = createAsyncThunk(
  'notifications/testPushConfig',
  async ({ id, testDeviceToken }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.testPushConfig(id, testDeviceToken);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendNotification = createAsyncThunk(
  'notifications/sendNotification',
  async (notificationData, { rejectWithValue }) => {
    try {
      const data = await notificationApi.sendNotification(notificationData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchConfigurationSummary = createAsyncThunk(
  'notifications/fetchConfigurationSummary',
  async (companyId, { rejectWithValue }) => {
    try {
      const data = await notificationApi.getConfigurationSummary(companyId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunks for route management
export const fetchRoutesByTemplate = createAsyncThunk(
  'notifications/fetchRoutesByTemplate',
  async ({ templateId, channel }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.getRoutesByTemplate(templateId, channel);
      return { templateId, routes: data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAvailableRoutes = createAsyncThunk(
  'notifications/fetchAvailableRoutes',
  async ({ companyId, channel }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.getAvailableRoutes(companyId, channel);
      return { channel, routes: data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createRoute = createAsyncThunk(
  'notifications/createRoute',
  async (routeData, { rejectWithValue }) => {
    try {
      const data = await notificationApi.createRoute(routeData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateRoute = createAsyncThunk(
  'notifications/updateRoute',
  async ({ id, routeData }, { rejectWithValue }) => {
    try {
      const data = await notificationApi.updateRoute(id, routeData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRoute = createAsyncThunk(
  'notifications/deleteRoute',
  async (id, { rejectWithValue }) => {
    try {
      await notificationApi.deleteRoute(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Templates
  templates: {
    data: [],
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    loading: false,
    error: null,
  },

  // Configurations
  emailConfigs: {
    data: [],
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    loading: false,
    error: null,
  },

  smsConfigs: {
    data: [],
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    loading: false,
    error: null,
  },

  // Template variables
  templateVariables: {
    data: [],
    loading: false,
    error: null,
  },

  // Template routes
  templateRoutes: {
    data: [],
    loading: false,
    error: null,
  },

  // Configuration summary
  configurationSummary: {
    data: null,
    loading: false,
    error: null,
  },

  // Send notification
  sendNotification: {
    loading: false,
    error: null,
    success: false,
  },

  // UI state
  selectedTemplate: null,
  selectedEmailConfig: null,
  selectedSmsConfig: null,
  filters: {
    search: '',
    channel: null,
    applicationName: '',
    gateway: null,
  },
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state, action) => {
      const { section } = action.payload;
      if (state[section]) {
        state[section].error = null;
      }
    },
    setSelectedTemplate: (state, action) => {
      state.selectedTemplate = action.payload;
    },
    setSelectedEmailConfig: (state, action) => {
      state.selectedEmailConfig = action.payload;
    },
    setSelectedSmsConfig: (state, action) => {
      state.selectedSmsConfig = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    resetSendNotification: (state) => {
      state.sendNotification = initialState.sendNotification;
    },
  },
  extraReducers: (builder) => {
    // Templates
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.templates.loading = true;
        state.templates.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templates.loading = false;
        state.templates.data = action.payload.content || [];
        state.templates.totalElements = action.payload.totalElements || 0;
        state.templates.totalPages = action.payload.totalPages || 0;
        state.templates.currentPage = action.payload.number || 0;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.templates.loading = false;
        state.templates.error = action.payload;
      })

      .addCase(createTemplate.pending, (state) => {
        state.templates.loading = true;
        state.templates.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.templates.loading = false;
        if (action.payload.data) {
          state.templates.data.unshift(action.payload.data);
        }
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.templates.loading = false;
        state.templates.error = action.payload;
      })

      .addCase(updateTemplate.fulfilled, (state, action) => {
        if (action.payload.data) {
          const index = state.templates.data.findIndex(template => template.id === action.payload.data.id);
          if (index !== -1) {
            state.templates.data[index] = action.payload.data;
          }
        }
      })

      .addCase(deleteTemplate.pending, (state) => {
        state.templates.loading = true;
        state.templates.error = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.templates.loading = false;
        state.templates.data = state.templates.data.filter(template => template.id !== action.payload);
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.templates.loading = false;
        state.templates.error = action.payload;
      })

      .addCase(toggleTemplateStatus.fulfilled, (state, action) => {
        if (action.payload.data) {
          const index = state.templates.data.findIndex(template => template.id === action.payload.data.id);
          if (index !== -1) {
            state.templates.data[index] = action.payload.data;
          }
        }
      })

      // Email Configurations
      .addCase(fetchEmailConfigs.pending, (state) => {
        state.emailConfigs.loading = true;
        state.emailConfigs.error = null;
      })
      .addCase(fetchEmailConfigs.fulfilled, (state, action) => {
        state.emailConfigs.loading = false;
        // Handle both paginated and direct array responses
        if (action.payload && action.payload.content) {
          // Paginated response
          state.emailConfigs.data = action.payload.content || [];
          state.emailConfigs.totalElements = action.payload.totalElements || 0;
          state.emailConfigs.totalPages = action.payload.totalPages || 0;
          state.emailConfigs.currentPage = action.payload.number || 0;
        } else {
          // Direct array response
          state.emailConfigs.data = Array.isArray(action.payload) ? action.payload : [];
          state.emailConfigs.totalElements = Array.isArray(action.payload) ? action.payload.length : 0;
          state.emailConfigs.totalPages = 1;
          state.emailConfigs.currentPage = 0;
        }
      })
      .addCase(fetchEmailConfigs.rejected, (state, action) => {
        state.emailConfigs.loading = false;
        state.emailConfigs.error = action.payload;
      })
      .addCase(createEmailConfig.pending, (state) => {
        state.emailConfigs.loading = true;
        state.emailConfigs.error = null;
      })
      .addCase(createEmailConfig.fulfilled, (state, action) => {
        state.emailConfigs.loading = false;
        if (action.payload.data) {
          state.emailConfigs.data.unshift(action.payload.data);
        }
      })
      .addCase(createEmailConfig.rejected, (state, action) => {
        state.emailConfigs.loading = false;
        state.emailConfigs.error = action.payload;
      })
      .addCase(updateEmailConfig.fulfilled, (state, action) => {
        if (action.payload.data) {
          const index = state.emailConfigs.data.findIndex(config => config.id === action.payload.data.id);
          if (index !== -1) {
            state.emailConfigs.data[index] = action.payload.data;
          }
        }
      })
      .addCase(deleteEmailConfig.fulfilled, (state, action) => {
        state.emailConfigs.data = state.emailConfigs.data.filter(config => config.id !== action.payload);
      })
      .addCase(toggleEmailConfigStatus.fulfilled, (state, action) => {
        if (action.payload.data) {
          const index = state.emailConfigs.data.findIndex(config => config.id === action.payload.data.id);
          if (index !== -1) {
            state.emailConfigs.data[index] = action.payload.data;
          }
        }
      })
      .addCase(testEmailConfig.fulfilled, (state, action) => {
        // Test email API returns a string message, not a config object
        // No need to update the state, just let the component handle the success
        console.log('Test email response:', action.payload);
      })
      .addCase(testEmailConfig.rejected, (state, action) => {
        console.error('Test email failed:', action.payload);
      })

      // SMS Configurations
      .addCase(fetchSmsConfigs.pending, (state) => {
        state.smsConfigs.loading = true;
        state.smsConfigs.error = null;
      })
      .addCase(fetchSmsConfigs.fulfilled, (state, action) => {
        state.smsConfigs.loading = false;
        // Handle both paginated and direct array responses
        if (action.payload && action.payload.content) {
          // Paginated response
          state.smsConfigs.data = action.payload.content || [];
          state.smsConfigs.totalElements = action.payload.totalElements || 0;
          state.smsConfigs.totalPages = action.payload.totalPages || 0;
          state.smsConfigs.currentPage = action.payload.number || 0;
        } else {
          // Direct array response
          state.smsConfigs.data = Array.isArray(action.payload) ? action.payload : [];
          state.smsConfigs.totalElements = Array.isArray(action.payload) ? action.payload.length : 0;
          state.smsConfigs.totalPages = 1;
          state.smsConfigs.currentPage = 0;
        }
      })
      .addCase(fetchSmsConfigs.rejected, (state, action) => {
        state.smsConfigs.loading = false;
        state.smsConfigs.error = action.payload;
      })
      .addCase(createSmsConfig.pending, (state) => {
        state.smsConfigs.loading = true;
        state.smsConfigs.error = null;
      })
      .addCase(createSmsConfig.fulfilled, (state, action) => {
        state.smsConfigs.loading = false;
        if (action.payload.data) {
          state.smsConfigs.data.unshift(action.payload.data);
        }
      })
      .addCase(createSmsConfig.rejected, (state, action) => {
        state.smsConfigs.loading = false;
        state.smsConfigs.error = action.payload;
      })
      .addCase(updateSmsConfig.fulfilled, (state, action) => {
        if (action.payload.data) {
          const index = state.smsConfigs.data.findIndex(config => config.id === action.payload.data.id);
          if (index !== -1) {
            state.smsConfigs.data[index] = action.payload.data;
          }
        }
      })
      .addCase(deleteSmsConfig.fulfilled, (state, action) => {
        state.smsConfigs.data = state.smsConfigs.data.filter(config => config.id !== action.payload);
      })
      .addCase(toggleSmsConfigStatus.fulfilled, (state, action) => {
        // Handle different response formats from the backend
        const updatedConfig = action.payload.data || action.payload;
        if (updatedConfig && updatedConfig.id) {
          const index = state.smsConfigs.data.findIndex(config => config.id === updatedConfig.id);
          if (index !== -1) {
            state.smsConfigs.data[index] = updatedConfig;
          }
        }
      })
      .addCase(testSmsConfig.fulfilled, (state, action) => {
        if (action.payload.data) {
          const index = state.smsConfigs.data.findIndex(config => config.id === action.payload.data.id);
          if (index !== -1) {
            state.smsConfigs.data[index] = action.payload.data;
          }
        }
      })

      // Configuration Summary
      .addCase(fetchConfigurationSummary.pending, (state) => {
        state.configurationSummary.loading = true;
        state.configurationSummary.error = null;
      })
      .addCase(fetchConfigurationSummary.fulfilled, (state, action) => {
        state.configurationSummary.loading = false;
        state.configurationSummary.data = action.payload;
      })
      .addCase(fetchConfigurationSummary.rejected, (state, action) => {
        state.configurationSummary.loading = false;
        state.configurationSummary.error = action.payload;
      })

      // Send Notification
      .addCase(sendNotification.pending, (state) => {
        state.sendNotification.loading = true;
        state.sendNotification.error = null;
        state.sendNotification.success = false;
      })
      .addCase(sendNotification.fulfilled, (state) => {
        state.sendNotification.loading = false;
        state.sendNotification.success = true;
      })
      .addCase(sendNotification.rejected, (state, action) => {
        state.sendNotification.loading = false;
        state.sendNotification.error = action.payload;
      })

      // Fetch Configurations by Channel
      .addCase(fetchConfigurationsByChannel.pending, (state) => {
        state.emailConfigs.loading = true;
        state.smsConfigs.loading = true;
      })
      .addCase(fetchConfigurationsByChannel.fulfilled, (state, action) => {
        const { channel, configurations } = action.payload;
        if (channel === 'EMAIL') {
          state.emailConfigs.data = configurations;
          state.emailConfigs.loading = false;
        } else if (channel === 'SMS') {
          state.smsConfigs.data = configurations;
          state.smsConfigs.loading = false;
        }
      })
      .addCase(fetchConfigurationsByChannel.rejected, (state, action) => {
        state.emailConfigs.loading = false;
        state.smsConfigs.loading = false;
        state.emailConfigs.error = action.payload;
        state.smsConfigs.error = action.payload;
      })

      // Template Routes
      .addCase(fetchTemplateRoutes.pending, (state) => {
        state.templateRoutes.loading = true;
        state.templateRoutes.error = null;
      })
      .addCase(fetchTemplateRoutes.fulfilled, (state, action) => {
        state.templateRoutes.loading = false;
        state.templateRoutes.data = action.payload || [];
      })
      .addCase(fetchTemplateRoutes.rejected, (state, action) => {
        state.templateRoutes.loading = false;
        state.templateRoutes.error = action.payload;
      })
      .addCase(updateTemplateRoutes.fulfilled, (state, action) => {
        if (action.payload.data) {
          const index = state.templateRoutes.data.findIndex(route => route.id === action.payload.data.id);
          if (index !== -1) {
            state.templateRoutes.data[index] = action.payload.data;
          }
        }
      })

      // Routes by Template
      .addCase(fetchRoutesByTemplate.pending, (state) => {
        state.templateRoutes.loading = true;
        state.templateRoutes.error = null;
      })
      .addCase(fetchRoutesByTemplate.fulfilled, (state, action) => {
        const { templateId, routes } = action.payload;
        const templateIndex = state.templates.data.findIndex(template => template.id === templateId);
        if (templateIndex !== -1) {
          state.templates.data[templateIndex].routes = routes;
        }
        state.templateRoutes.loading = false;
      })
      .addCase(fetchRoutesByTemplate.rejected, (state, action) => {
        state.templateRoutes.loading = false;
        state.templateRoutes.error = action.payload;
      })

      // Available Routes
      .addCase(fetchAvailableRoutes.pending, (state) => {
        state.templateRoutes.loading = true;
        state.templateRoutes.error = null;
      })
      .addCase(fetchAvailableRoutes.fulfilled, (state, action) => {
        const { channel, routes } = action.payload;
        if (channel === 'EMAIL') {
          state.emailConfigs.routes = routes;
        } else if (channel === 'SMS') {
          state.smsConfigs.routes = routes;
        }
        state.templateRoutes.loading = false;
      })
      .addCase(fetchAvailableRoutes.rejected, (state, action) => {
        state.templateRoutes.loading = false;
        state.templateRoutes.error = action.payload;
      });
  },
});

export const {
  clearError,
  setSelectedTemplate,
  setSelectedEmailConfig,
  setSelectedSmsConfig,
  setFilters,
  clearFilters,
  resetSendNotification,
} = notificationSlice.actions;

export default notificationSlice.reducer;
